import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { newsletterApi } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';

const STORAGE_KEY = 'ss_popup_seen_v1';

export const WelcomePopup: React.FC = () => {
  const { settings } = useSettings();
  const { showSuccess, showError } = useNotification();
  const popup = settings?.popup;
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!popup?.enabled) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const timer = setTimeout(() => setIsOpen(true), 800); // slight delay after first paint
      return () => clearTimeout(timer);
    }
  }, [popup?.enabled]);

  const close = () => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!popup?.enabled || !isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 animate-fade-in">
        <div className="relative h-64 md:h-full">
          {popup.imageUrl ? (
            <img src={popup.imageUrl} alt={popup.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gray-200" />
          )}
        </div>
        <div className="p-6 md:p-8 relative flex flex-col gap-4">
          <button
            aria-label="Close"
            onClick={close}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
          >
            <X size={22} />
          </button>
          <div className="space-y-2">
            <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A]">{popup.title || 'Welcome'}</h3>
            {popup.subtitle && <p className="text-lg text-gray-700 leading-snug">{popup.subtitle}</p>}
            {popup.body && <p className="text-sm text-gray-600">{popup.body}</p>}
          </div>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              (async () => {
                try {
                  const { data, error } = await newsletterApi.subscribe(email);
                  if (error) {
                    showError(error);
                  } else {
                    showSuccess(data?.message || 'Subscribed! Check your inbox for updates.');
                  }
                } catch (err) {
                  showError('Failed to subscribe. Please try again.');
                } finally {
                  close();
                }
              })();
            }}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-[#4A2C2A] focus:ring-1 focus:ring-[#4A2C2A]"
            />
            <button
              type="submit"
              className="w-full bg-[#0F172A] text-white font-semibold py-3 rounded-lg hover:bg-[#122032] transition-colors"
            >
              {popup.ctaText || 'Submit'}
            </button>
          </form>
          <p className="text-xs text-gray-500 leading-relaxed">
            By signing up, you agree to receive marketing emails. View our privacy policy and terms of service for more info.
          </p>
        </div>
      </div>
    </div>
  );
};
